import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { Loader2 } from "lucide-react";

/**
 * BillingGuard - Protects routes that require active subscription
 * 
 * Uses SubscriptionContext as single source of truth.
 * Does NOT fetch subscription status directly.
 * 
 * Allowed statuses:
 * - trialing: During 7-day $1 intro period
 * - intro: Same as trialing (alias)
 * - active: Paid subscription
 * - past_due: Payment failed but within 48h grace period
 * 
 * Blocked:
 * - inactive: Never subscribed
 * - canceled: Subscription ended
 * - past_due with grace expired (>48h)
 */
export default function BillingGuard({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { 
    status, 
    isLoading: subscriptionLoading, 
    hasAccess,
    pastDueGraceExpired 
  } = useSubscription();
  const location = useLocation();

  // Still loading auth or subscription
  if (authLoading || subscriptionLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f5f5f0]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // For past_due with expired grace, pass state to show payment failed message
  if (status === "past_due" && pastDueGraceExpired) {
    return <Navigate to="/complete-signup" replace state={{ payment_failed: true }} />;
  }

  // Check if user has access
  if (!hasAccess) {
    return <Navigate to="/complete-signup" replace />;
  }

  // Redirect to onboarding if not completed
  if (!user.onboarding_completed && location.pathname !== "/onboarding") {
    return <Navigate to="/onboarding" replace />;
  }

  // User has active subscription - render children
  return children;
}
